import { IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { merge } from "lodash-es";
import { motion } from "motion/react";
import { useShallow } from "zustand/shallow";
import { NumberTicker } from "@/components/magicui/number-ticker";
import { checkCircleIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { userGroupIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { queryOptions_coreValues } from "@/lib/query/functions/core-values/all";
import { authSelectors, useAuthStore } from "@/lib/store/auth";
import { cx } from "@/lib/style/cva.config";

export function CoreValueRankingRow({
	data,
	enableAnimation = true,
	...props
}: React.ComponentProps<typeof motion.li> & {
	data?: {
		type?: "reflection" | "friend-check" | "saved" | "compiled" | undefined;
		name?: string | null | undefined;
		number?: number | null | undefined;
		score?: number | null | undefined;
		reason?: string | null | undefined;
		customName?: string | null | undefined;
		matchScore?: number | null | undefined;
	};
	enableAnimation?: boolean;
}) {
	const { t, i18n } = useTranslation();
	const isAuthenticated = useAuthStore(useShallow(authSelectors.isAuthenticated));
	const queryCoreValues = useQuery({ ...queryOptions_coreValues(), enabled: isAuthenticated });
	const coreValue = queryCoreValues.data?.find((coreValue) => coreValue.name === data?.name);

	return (
		<motion.li
			{...props}
			className={cx(
				"bg-brown-100 flex cursor-pointer items-center justify-between gap-2 rounded-xl px-1 py-3",
				props.className,
			)}
			{...(enableAnimation
				? {
						initial: merge(
							{
								opacity: 0,
								scaleX: 0.75,
								y: "1rem",
								display: data?.type === "compiled" ? "flex" : "none",
							} satisfies (typeof props)["initial"],
							props.initial,
						),
						animate: merge(
							{ opacity: 1, scaleX: 1, y: 0, display: "flex" } satisfies (typeof props)["animate"],
							props.animate,
						),
					}
				: {})}
		>
			<span className="text-brown-700 flex flex-row gap-2 font-semibold">
				<span className="block min-w-6 text-end">{!!data?.number && `${data.number}.`}</span>
				<span className="block min-w-5 text-center">{coreValue?.emoji || "❓"}</span>
				<span>{data?.customName || data?.name || ""}</span>
			</span>

			<div className="flex justify-end">
				{data?.matchScore && (
					<span className="text-brown-400 text-end font-semibold">
						{t("core-values.accuracy.small", {
							accuracy: data.matchScore,
						})}
					</span>
				)}

				{data?.type === "friend-check" ? (
					<IonIcon icon={userGroupIcon} className="text-brown-400 size-5" />
				) : data?.type === "compiled" ? (
					<>
						{data?.customName ? (
							<IonIcon icon={checkCircleIcon} className="text-brown-400 size-5" />
						) : (
							<div className="border-brown-400 size-4 rounded-full border-2" />
						)}
					</>
				) : (
					<NumberTicker
						className="text-brown-400 text-end font-semibold"
						value={data?.score ?? 0}
						getTextContent={(value) =>
							Intl.NumberFormat(i18n.language, { style: "unit", unit: "percent" }).format(
								value.toFixed(0),
							)
						}
						initial="hidden"
						animate={data?.score ? "visible" : "hidden"}
						variants={{
							visible: { opacity: 1, pointerEvents: "auto" },
							hidden: { opacity: 0, pointerEvents: "none" },
						}}
						transition={{ duration: 0.25, delay: props.transition?.delay }}
						duration={0.25}
						checkInView={false}
						delay={props.transition?.delay}
					/>
				)}
			</div>
		</motion.li>
	);
}
